import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Upload,
  FileSpreadsheet,
  Loader2,
  CheckCircle,
  AlertCircle,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api';
import UploadHistorySection from '../components/UploadHistorySection';

const platformOptions = [
  { value: 'Amazon', label: 'Amazon', icon: '🛒' },
  { value: 'Flipkart', label: 'Flipkart', icon: '📦' },
  { value: 'Blinkit', label: 'Blinkit', icon: '⚡' },
  { value: 'Zepto', label: 'Zepto', icon: '🚀' },
  { value: 'Wellversed.in', label: 'Wellversed.in (D2C)', icon: '🌿' },
];

export default function UploadReviewsPage() {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [uploadHistoryRefresh, setUploadHistoryRefresh] = useState(0);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(csv|xlsx|xls)$/i)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }
    if (!selectedPlatform) {
      toast.error('Please select a platform before uploading');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 100);

      const response = await apiFetch(`/api/reviews/upload?platform=${encodeURIComponent(selectedPlatform)}`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        const data = await response.json();
        setUploadResult({ success: true, ...data });
        toast.success(`Uploaded ${data.count} reviews successfully`);
        setUploadHistoryRefresh((n) => n + 1);
      } else {
        const error = await response.json();
        setUploadResult({ success: false, error: error.detail });
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      setUploadResult({ success: false, error: 'Upload failed' });
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const analyzeReviews = async () => {
    setAnalyzing(true);
    let totalAnalyzed = 0;
    let remaining = 1;
    try {
      while (remaining > 0) {
        const response = await apiFetch('/api/reviews/analyze?batch_size=500', { method: 'POST' });
        if (response.ok) {
          const data = await response.json();
          totalAnalyzed += data.count;
          remaining = data.total_remaining || 0;
          if (data.count > 0) {
            toast.info(`Analyzed ${totalAnalyzed} reviews... ${remaining > 0 ? `${remaining} remaining` : 'Complete!'}`);
          }
          if (remaining === 0 || data.count === 0) break;
        } else {
          toast.error('Analysis failed');
          break;
        }
      }
      if (totalAnalyzed > 0) {
        toast.success(`Successfully analyzed ${totalAnalyzed} reviews with AI`);
      } else {
        toast.info('No unanalyzed reviews found');
      }
    } catch (error) {
      toast.error('Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="upload-reviews-page">
      <div>
        <h1 className="text-3xl font-bold font-heading tracking-tight">Upload Reviews</h1>
        <p className="text-muted-foreground mt-1">
          Upload and analyze review data from your marketplaces
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="card-hover" data-testid="upload-zone-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-[#2eb4ac]" />
                Upload Review Data
              </CardTitle>
              <CardDescription>
                Upload CSV or Excel files with review data from your marketplaces
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Select Platform <span className="text-destructive">*</span>
                </label>
                <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                  <SelectTrigger className="w-full md:w-64" data-testid="platform-select">
                    <SelectValue placeholder="Choose platform..." />
                  </SelectTrigger>
                  <SelectContent>
                    {platformOptions.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        <span className="flex items-center gap-2">
                          <span>{p.icon}</span>
                          <span>{p.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  All reviews in this file will be tagged with the selected platform
                </p>
              </div>

              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? 'border-[#2eb4ac] bg-[#2eb4ac]/5'
                    : selectedPlatform
                      ? 'border-border hover:border-[#2eb4ac]/50'
                      : 'border-border opacity-50 cursor-not-allowed'
                }`}
                onDragEnter={selectedPlatform ? handleDrag : undefined}
                onDragLeave={selectedPlatform ? handleDrag : undefined}
                onDragOver={selectedPlatform ? handleDrag : undefined}
                onDrop={selectedPlatform ? handleDrop : undefined}
                data-testid="upload-dropzone"
              >
                <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm text-muted-foreground mb-2">
                  {selectedPlatform
                    ? 'Drag and drop your file here, or'
                    : 'Select a platform first to upload'}
                </p>
                <label>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileInput}
                    className="hidden"
                    disabled={uploading || !selectedPlatform}
                    data-testid="file-input"
                  />
                  <Button variant="outline" className="cursor-pointer" disabled={uploading || !selectedPlatform} asChild>
                    <span>Browse Files</span>
                  </Button>
                </label>
              </div>

              {uploading && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <Progress value={uploadProgress} className="h-2" />
                </div>
              )}

              {uploadResult && (
                <div
                  className={`p-4 rounded-lg flex items-center gap-3 ${
                    uploadResult.success
                      ? 'bg-[#2eb4ac]/10 text-[#2eb4ac]'
                      : 'bg-destructive/10 text-destructive'
                  }`}
                  data-testid="upload-result"
                >
                  {uploadResult.success ? (
                    <><CheckCircle className="w-5 h-5" /><span>Successfully uploaded {uploadResult.count} reviews.</span></>
                  ) : (
                    <><AlertCircle className="w-5 h-5" /><span>{uploadResult.error}</span></>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="card-hover" data-testid="ai-analysis-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#2eb4ac]" />
                AI Analysis
              </CardTitle>
              <CardDescription>
                Run AI analysis on uploaded reviews to detect sentiment, issues, and topics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={analyzeReviews}
                disabled={analyzing}
                className="bg-[#2eb4ac] hover:bg-[#25908a] w-full"
                data-testid="analyze-reviews-btn"
              >
                {analyzing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />Analyze with AI</>
                )}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Analyzes all unanalyzed reviews using Groq AI
              </p>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="font-heading text-sm">Supported Columns</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {['Brand', 'Product Name', 'Rating', 'Review Text', 'Review Date', 'Total Rating'].map((col) => (
                  <Badge key={col} variant="secondary">{col}</Badge>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                No columns are mandatory — include whichever data you have. <span className="font-medium text-foreground/70">Total Rating</span> (cumulative rating count) is supported for Blinkit &amp; Zepto.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <UploadHistorySection refreshKey={uploadHistoryRefresh} />
    </div>
  );
}
